import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
    Switch, Drawer, Form, Input,
    message, Popconfirm, Space, Button,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import dayjs from 'dayjs';
import { LeftSide } from '../../components/LeftSide/LeftSide';
import { Navi } from '../../components/Navi/Navi';
import { RightBar } from '../../components/RightBar/RightBar';
import { ScrollToTop } from '../../components/ScrollToTop/ScrollToTop';
import { useMinLoadingDisplay } from '../../hooks/useMinLoadingDisplay';
import api from '../../utils/api';
import { getServerApiRootUrl } from '../../utils/adminApiBase';
import '../../styles/common.css';
import '../Users/User.css';

const SERVER_ROOT = getServerApiRootUrl();
const PAGE_SIZE = 10;

const QUILL_FORMATS = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'color', 'background', 'list', 'align', 'link', 'image',
];

export const Notices = ({ title = '공지사항 관리' }) => {
    const [list, setList] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const showListLoading = useMinLoadingDisplay(isLoading);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [content, setContent] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [form] = Form.useForm();
    const quillRef = useRef(null);

    const totalPages = Math.ceil(total / PAGE_SIZE);

    const fetchList = useCallback(async (p = 1, search = '') => {
        setIsLoading(true);
        try {
            const params = { page: p, limit: PAGE_SIZE };
            if (search) params.search = search;
            const res = await api.get('/notices', { params });
            setList(res.data.list);
            setTotal(res.data.total);
        } catch {
            message.error('목록 조회에 실패했습니다.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { fetchList(page, appliedSearch); }, [fetchList, page, appliedSearch]);

    const handleSearch = () => {
        setPage(1);
        setAppliedSearch(searchTerm);
    };

    const handleReset = () => {
        setSearchTerm('');
        setAppliedSearch('');
        setPage(1);
    };

    const handlePageChange = (p) => setPage(p);

    const renderPagination = () => {
        if (totalPages <= 0) return null;
        const pages = [];
        let start = Math.max(1, page - 5);
        let end = Math.min(totalPages, start + 9);
        if (end - start < 9 && totalPages > 10) start = Math.max(1, end - 9);
        for (let i = start; i <= end; i++) pages.push(i);

        const pageBtn = (p, label, disabled = false, icon = false) => (
            <li key={label} className={`page-item ${disabled ? 'disabled' : ''}`}>
                <Link
                    className="page-link"
                    onClick={e => { e.preventDefault(); if (!disabled) handlePageChange(p); }}
                    title={icon ? label : undefined}
                >
                    {icon ? <i className={label}></i> : label}
                </Link>
            </li>
        );

        return (
            <nav aria-label="공지사항 페이지네이션">
                <ul className="pagination pagination-sm justify-content-center mt-3">
                    {pageBtn(1, 'fas fa-angle-double-left', page === 1, true)}
                    {pageBtn(page - 1, '이전', page === 1)}
                    {pages.map(num => (
                        <li key={num} className={`page-item ${page === num ? 'active' : ''}`}>
                            <Link className="page-link" onClick={e => { e.preventDefault(); handlePageChange(num); }}>{num}</Link>
                        </li>
                    ))}
                    {pageBtn(page + 1, '다음', page === totalPages)}
                    {pageBtn(totalPages, 'fas fa-angle-double-right', page === totalPages, true)}
                </ul>
            </nav>
        );
    };

    const imageHandler = useCallback(() => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/jpeg,image/png,image/gif,image/webp');
        input.click();
        input.onchange = async () => {
            const file = input.files?.[0];
            if (!file) return;
            const formData = new FormData();
            formData.append('image', file);
            try {
                const res = await api.post('/notices/upload', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                const url = `${SERVER_ROOT}${res.data.url}`;
                const quill = quillRef.current?.getEditor();
                if (!quill) return;
                const range = quill.getSelection(true);
                quill.insertEmbed(range.index, 'image', url);
                quill.setSelection(range.index + 1);
            } catch {
                message.error('이미지 업로드에 실패했습니다.');
            }
        };
    }, []);

    const quillModules = useMemo(() => ({
        toolbar: {
            container: [
                [{ header: [1, 2, 3, false] }],
                ['bold', 'italic', 'underline', 'strike'],
                [{ color: [] }, { background: [] }],
                [{ list: 'ordered' }, { list: 'bullet' }],
                [{ align: [] }],
                ['link', 'image'],
                ['clean'],
            ],
            handlers: { image: imageHandler },
        },
    }), [imageHandler]);

    const openCreate = () => {
        setEditId(null);
        form.resetFields();
        setContent('');
        setDrawerOpen(true);
    };

    const openEdit = async (id) => {
        try {
            const res = await api.get(`/notices/${id}`);
            const d = res.data;
            setEditId(id);
            form.setFieldsValue({
                title: d.title,
                is_published: !!d.is_published,
                is_top: !!d.is_top,
                is_notice: !!d.is_notice,
            });
            setContent(d.content || '');
            setDrawerOpen(true);
        } catch {
            message.error('데이터 조회에 실패했습니다.');
        }
    };

    const handleSave = async () => {
        let values;
        try {
            values = await form.validateFields();
        } catch {
            return;
        }
        setSaving(true);
        try {
            const payload = {
                title: values.title,
                content,
                is_published: values.is_published ? 1 : 0,
                is_top: values.is_top ? 1 : 0,
                is_notice: values.is_notice ? 1 : 0,
            };
            if (editId) {
                await api.put(`/notices/${editId}`, payload);
                message.success('수정되었습니다.');
            } else {
                await api.post('/notices', payload);
                message.success('등록되었습니다.');
                setPage(1);
            }
            setDrawerOpen(false);
            fetchList(editId ? page : 1);
        } catch {
            message.error('저장에 실패했습니다.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await api.delete(`/notices/${id}`);
            message.success('삭제되었습니다.');
            fetchList(page);
        } catch {
            message.error('삭제에 실패했습니다.');
        }
    };

    const handleTogglePublish = async (id, checked) => {
        try {
            await api.patch(`/notices/${id}/publish`, { is_published: checked ? 1 : 0 });
            setList(prev => prev.map(r => r.id === id ? { ...r, is_published: checked ? 1 : 0 } : r));
        } catch {
            message.error('변경에 실패했습니다.');
        }
    };

    const handleToggleTop = async (id, checked) => {
        try {
            await api.patch(`/notices/${id}/top`, { is_top: checked ? 1 : 0 });
            setList(prev => {
                const updated = prev.map(r => r.id === id ? { ...r, is_top: checked ? 1 : 0 } : r);
                return updated.sort((a, b) => {
                    if (b.is_top !== a.is_top) return b.is_top - a.is_top;
                    return new Date(b.created_at) - new Date(a.created_at);
                });
            });
        } catch {
            message.error('변경에 실패했습니다.');
        }
    };

    const handleToggleNotice = async (id, checked) => {
        try {
            await api.patch(`/notices/${id}/notice`, { is_notice: checked ? 1 : 0 });
            setList(prev => prev.map(r => r.id === id ? { ...r, is_notice: checked ? 1 : 0 } : r));
        } catch {
            message.error('변경에 실패했습니다.');
        }
    };

    return (
        <>
            <div id="wrapper">
                <Navi title={title} />
                <LeftSide />
                <div className="content-page">
                    <div className="content">
                        <div className="container-fluid users-admin-page">
                            <div className="row">
                                <div className="col-xl-12">
                                    <div className="card">
                                        <div className="card-body">
                                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-1 mb-3">
                                                <div className="d-flex flex-wrap align-items-center gap-1">
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        placeholder="제목 검색"
                                                        value={searchTerm}
                                                        onChange={(e) => setSearchTerm(e.target.value)}
                                                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                                                        style={{ width: 220 }}
                                                    />
                                                    <button className="btn btn-primary btn-sm" type="button" onClick={handleSearch}>
                                                        <i className="fas fa-search"></i> 검색
                                                    </button>
                                                    <button className="btn btn-secondary btn-sm" type="button" onClick={handleReset}>
                                                        <i className="fas fa-sync-alt"></i> 초기화
                                                    </button>
                                                </div>
                                                <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                                                    새 공지 등록
                                                </Button>
                                            </div>

                                            <div className="table-responsive" style={{ overflowX: 'auto' }}>
                                                {showListLoading ? (
                                                    <div className="text-center p-4">
                                                        <div className="loading-spinner loading-spinner-lg" role="status" aria-live="polite">
                                                            <span className="visually-hidden">데이터를 불러오는 중입니다.</span>
                                                        </div>
                                                        <p className="mt-2">데이터를 불러오는 중입니다...</p>
                                                    </div>
                                                ) : (
                                                    <table className="table table-bordered table-hover table-sm align-middle">
                                                        <thead className="table-light">
                                                            <tr>
                                                                <th className="text-center text-nowrap" width="6%">ID</th>
                                                                <th className="text-center">제목</th>
                                                                <th className="text-center text-nowrap" width="9%">발행</th>
                                                                <th className="text-center text-nowrap" width="9%">TOP</th>
                                                                <th className="text-center text-nowrap" width="9%">펼치기</th>
                                                                <th className="text-center text-nowrap" width="14%">등록일</th>
                                                                <th className="text-center text-nowrap" width="8%">관리</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {list.map((row) => (
                                                                <tr key={row.id}>
                                                                    <td className="text-center text-nowrap">{row.id}</td>
                                                                    <td className="text-start users-admin-cell-wrap">{row.title}</td>
                                                                    <td className="text-center">
                                                                        <Switch
                                                                            checked={!!row.is_published}
                                                                            onChange={(checked) => handleTogglePublish(row.id, checked)}
                                                                        />
                                                                    </td>
                                                                    <td className="text-center">
                                                                        <Switch
                                                                            checked={!!row.is_top}
                                                                            onChange={(checked) => handleToggleTop(row.id, checked)}
                                                                            checkedChildren="ON"
                                                                            unCheckedChildren="OFF"
                                                                        />
                                                                    </td>
                                                                    <td className="text-center">
                                                                        <Switch
                                                                            checked={!!row.is_notice}
                                                                            onChange={(checked) => handleToggleNotice(row.id, checked)}
                                                                            checkedChildren="ON"
                                                                            unCheckedChildren="OFF"
                                                                        />
                                                                    </td>
                                                                    <td className="text-center text-nowrap">
                                                                        {row.created_at ? dayjs(row.created_at).format('YYYY-MM-DD HH:mm') : '-'}
                                                                    </td>
                                                                    <td className="text-center">
                                                                        <Space>
                                                                            <Button
                                                                                size="small"
                                                                                type="primary"
                                                                                icon={<EditOutlined />}
                                                                                onClick={() => openEdit(row.id)}
                                                                            />
                                                                            <Popconfirm
                                                                                title="삭제하시겠습니까?"
                                                                                onConfirm={() => handleDelete(row.id)}
                                                                                okText="삭제"
                                                                                cancelText="취소"
                                                                                okButtonProps={{ danger: true }}
                                                                            >
                                                                                <Button size="small" danger icon={<DeleteOutlined />} />
                                                                            </Popconfirm>
                                                                        </Space>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                )}
                                            </div>

                                            {!showListLoading && renderPagination()}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <RightBar />
            <div className="rightbar-overlay" />
            <ScrollToTop />

            <Drawer
                title={editId ? '공지사항 수정' : '공지사항 등록'}
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                width={820}
                footer={
                    <div style={{ textAlign: 'right' }}>
                        <Space>
                            <Button onClick={() => setDrawerOpen(false)}>취소</Button>
                            <Button type="primary" loading={saving} onClick={handleSave}>저장</Button>
                        </Space>
                    </div>
                }
                destroyOnClose
            >
                <Form form={form} layout="vertical" initialValues={{ is_published: true, is_top: false, is_notice: false }}>
                    <Form.Item
                        name="title"
                        label="제목"
                        rules={[{ required: true, message: '제목을 입력하세요.' }]}
                    >
                        <Input placeholder="공지사항 제목을 입력하세요" />
                    </Form.Item>

                    <Form.Item label="내용" required>
                        <div style={{ border: '1px solid #d9d9d9', borderRadius: 6 }}>
                            <ReactQuill
                                ref={quillRef}
                                theme="snow"
                                value={content}
                                onChange={setContent}
                                modules={quillModules}
                                formats={QUILL_FORMATS}
                                style={{ height: 360 }}
                            />
                        </div>
                        <div style={{ height: 42 }} />
                    </Form.Item>

                    <Form.Item name="is_published" label="발행여부" valuePropName="checked">
                        <Switch checkedChildren="발행" unCheckedChildren="비발행" />
                    </Form.Item>

                    <Form.Item name="is_top" label="TOP" valuePropName="checked" extra="ON으로 설정하면 공지 목록 상단에 고정됩니다.">
                        <Switch checkedChildren="ON" unCheckedChildren="OFF" />
                    </Form.Item>

                    <Form.Item name="is_notice" label="펼치기" valuePropName="checked" extra="ON으로 설정하면 대시보드에서 아코디언(펼치기) 형태로 내용이 노출됩니다.">
                        <Switch checkedChildren="ON" unCheckedChildren="OFF" />
                    </Form.Item>
                </Form>
            </Drawer>
        </>
    );
};
